import { computed, inject, Injectable, signal } from '@angular/core';
import {
  ApprovalFlowDto,
  ApprovalState,
  ApprovalUserRole,
  ChangeActionitemStatusRequestDto,
  ChangeApprovalStateDto,
  Client,
  IApprovalFlowDto,
  OutOfServiceActionDto,
  ReactivateActionItemRequestDto,
  ReportType,
} from '@fdm/api-client';
import { AuthService, CurrentUserService, LoaderService, Role } from '@fdm/services';
import { ToastrService } from 'ngx-toastr';
import { catchError, EMPTY, finalize, first, map, mergeMap, of, Subject, switchMap, tap } from 'rxjs';
import { ReportFormStore } from './report-form.store';
import { CommonUserService } from '../../services/common-user.service'
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse } from '@fdm/shared';
import { FormValidationService } from './form-validation.service';

@Injectable({ providedIn: 'root' })
export class ApprovalFlowService {

  private _apiClient = inject(Client);
  private _toastr = inject(ToastrService);
  private _store = inject(ReportFormStore);
  private _auth = inject(AuthService);
  private _commonUser = inject(CommonUserService);
  private _userService = inject(CurrentUserService);
  private _dialog = inject(MatDialog);
  private _loader = inject(LoaderService);
  private _formValidatorService = inject(FormValidationService);

  general = toSignal(this._store.general$);
  // approvalFlowReport = toSignal(this._store.approvalFlow$);

  MOCKED_STORE_KEY = computed(() => this.general()?.id ? `approval-flow-events-${this.general()?.id}` : null);
  /**
   * Store User Info
   */
  // private _user = inject(CurrentUserService);

  /**
   * Store Apprpoval Flow Tab Data
   */
  private _approvalFlowReport = toSignal(this._store.approvalFlow$);

  /**
   * Approval Flow Step
   */
  step = computed(() => this.general()?.currentState);

  /**
   * Calculated Approval Flow Status
   */
  public status = computed(() => this.general()?.currentFDMStatus);

  /**
   * Approval Flow State (FDM EVENTS)
   */
  private _state = signal<IApprovalFlowDto | null>(null);

  private _approvalUserRole = signal<ApprovalUserRole | null>(null);

  // private roleTarget = signal<ApprovalUserRole | null>(null);

  private roleTarget: ApprovalUserRole | null = ApprovalUserRole.Initiator;

  /**
   * Concurrency Protection: Prevents multiple simultaneous state changes
   */
  private _isProcessingStateChange = signal<boolean>(false);

  /**
   * Concurrency Protection: Prevents concurrent _saveFlowEvents calls
   */
  private _saveFlowEventsInProgress = signal<boolean>(false);

  /**
   * Deferral control: when true, _saveFlowEvents() records a pending save
   * instead of immediately calling the API. Used so the approval-flow PUT
   * (/api/ApprovalFlow/{id}) only fires AFTER the state-change PUT
   * (/api/ApprovalFlow/{id}/state) succeeds.
   */
  private _deferFlowSave = false;

  /**
   * Tracks whether a deferred flow-events save is awaiting the state change.
   */
  private _flowSavePending = false;

  /**
   * State Rollback: Snapshot of approval flow state before changes
   */
  private _stateSnapshot: ApprovalFlowDto | null = null;

  /**
   * Public observable for components to track processing state
   */
  public isProcessing = computed(() => this._isProcessingStateChange());

  /**
   * Fetch Approval Flow Request
   */
  public flowEventsUpdated = new Subject<void>();

  /**
   * Update Closed On Date Event
   */
  public updateClosedOn = new Subject<void>();

  /**
   * Notify when bulk save update is complete
   */
  public saveComplete = new Subject<void>();

  endorsers = computed(() => {
    const authorizedUserEmail = this._userService.email()?.toLowerCase();
    const approvalState = this._approvalFlowReport();

    const endorsers = [
      {
        email: approvalState?.additionalEndorser1?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser1Endorsed,
      }, 
      {
        email: approvalState?.additionalEndorser2?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser2Endorsed,
      }, 
      {
        email: approvalState?.additionalEndorser3?.toLowerCase(),
        endorsed: approvalState?.additionalEndorser3Endorsed,
      }, 
    ];

    const haveEndorsers = endorsers.some(item => item.email != undefined);
    const activeEndorsers = endorsers.filter(item => item.email != undefined);
    const allApproved = activeEndorsers.every(item => item.endorsed);
    const isEndorser1Active = !endorsers[0].endorsed && authorizedUserEmail === endorsers[0].email;
    const isEndorser2Active = endorsers[0].endorsed && !endorsers[1].endorsed && authorizedUserEmail === endorsers[1].email;
    // const isEndorser2Active = true
    const isEndorser3Active = endorsers[1].endorsed && !endorsers[2].endorsed && authorizedUserEmail === endorsers[2].email;
    const isEndorserActive = haveEndorsers && (isEndorser1Active || isEndorser2Active || isEndorser3Active);

    return { haveEndorsers, allApproved, isEndorserActive, isEndorser1Active, isEndorser2Active, isEndorser3Active };
  });


  userRole(targetState: ApprovalState) {
    // SAFETY FIX: Clear state signal to prevent flag accumulation from previous operations
    // This ensures we start with a clean slate for each approval flow operation
    this._state.set({} as IApprovalFlowDto);
    
    // DEBUG LOGGING: Track userRole calls and state changes
    console.log('[userRole] ==> Called with targetState:', targetState);
    console.log('[userRole] State signal cleared and ready for new operation');
    console.log('[userRole] Current approvalFlowReport:', JSON.stringify(this._approvalFlowReport()));

    const currentStep = this.step();
    const targetStep = targetState;

    const reportType = this._store.reportType();
    const abcPrioritization = this._store.abcPrioritization();
    const criticality = this._store.criticality();
    const auth_level = this._store.assignedManagementAuthorizationLevel();
    const stageField = this._store.stageField();
    const approvalFlowReport = this._approvalFlowReport();
    const endorsers = this.endorsers();
    const currentApprovalUser = this._commonUser.getCurrentUserApprovalRole(approvalFlowReport, this.general())
    const getAllMatchingUserApprovalRoles = this._commonUser.getAllMatchingUserApprovalRoles(approvalFlowReport, this.general())
    const isHCE = criticality === 'IC3' || criticality === 'IC1' || criticality === 'IC2' || criticality === 'AC1' || criticality === 'AC2';

    const roles = this._auth.roles;

    const isAdmin = roles.includes(Role.Admin);

    // ITR/RTR: Ops Advisor approves in their own role unless the user is also the Superintendent or Admin
    const threatApproverRole = getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.OpsAdvisor) &&
      !getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.AreaOpsSuperintendent) &&
      !isAdmin
      ? ApprovalUserRole.OpsAdvisor
      : ApprovalUserRole.AreaOpsSuperintendent;

    // if (roles.includes(Role.Admin) && !roles.includes(Role.Creator)) {
    //   this.roleTarget = null;
    //   return;
    // }

    if (targetStep === ApprovalState.Cancelled || targetStep === ApprovalState.OutOfService) {
      this.roleTarget = ApprovalUserRole.Owner;
      return;
    }

    if (targetStep === ApprovalState.Recycle) {
      if (getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.Owner)){
        this.roleTarget = ApprovalUserRole.Owner;
      }
      else if( getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.Initiator)){
        this.roleTarget = ApprovalUserRole.Initiator;
      }
      else{
        this.roleTarget = getAllMatchingUserApprovalRoles[0];
      }
      return;
    }

    switch (currentStep) {
      case ApprovalState.Draft:
        this.roleTarget = ApprovalUserRole.Initiator;
        break;

      case ApprovalState.DraftedForReview:
        if (reportType === ReportType.AnomalyReport) this.roleTarget = ApprovalUserRole.AreaLeadInspEng

        if (
          reportType === ReportType.TaskDeferral || 
          reportType === ReportType.IntegrityThreat || 
          reportType === ReportType.ReliabilityThreat || 
          reportType === ReportType.ReliabilityDeficiency || 
          reportType === ReportType.IntegrityDeficiency
        ) {
          if (
            !approvalFlowReport?.hasAreaLeadApproved && 
            approvalFlowReport?.areaLeadInspEng != null &&
            ( getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.AreaLeadInspEng) || isAdmin )
          ) {
            this.roleTarget = ApprovalUserRole.AreaLeadInspEng;
            this._state.update(s => ({ ...(s ?? {}), hasAreaLeadApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          } else {
            if (reportType === ReportType.TaskDeferral || reportType === ReportType.ReliabilityThreat || reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
              this.roleTarget = ApprovalUserRole.InspEngSupervisor
            }
          }
        };

        break;

      case ApprovalState.SubmittedToOpsAdvisor:
        if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
          this.roleTarget = ApprovalUserRole.OpsAdvisor;
        }

        if (reportType === ReportType.TaskDeferral || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
          this.roleTarget = ApprovalUserRole.OpsAdvisor;
        };

        // RIPT/Risk scores risk evaluation for IDR/RDR/TDR
        if ((reportType === ReportType.TaskDeferral || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) && this._auth.isInRole(Role.RIPTRisk)) {
          this.roleTarget = ApprovalUserRole.FastRepi;
        }

        break;

      case ApprovalState.PlanConfirmed:
        if (reportType === ReportType.AnomalyReport) {
          this.roleTarget = ApprovalUserRole.InspEngSupervisor;
        }

        if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) { if (this._auth.isInRole(Role.RIPTRisk)) { this.roleTarget = ApprovalUserRole.FastRepi } }

        if (reportType === ReportType.TaskDeferral) {
          if (
            isHCE &&
            (auth_level === 'A' || auth_level === 'B' || auth_level === 'C' || auth_level === 'D') &&
            !approvalFlowReport?.hasHCEPOApproved
          ) {
            // this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
            // this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
            // this._saveFlowEvents();

            // Asset Type HCEPO
            if (!approvalFlowReport?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // New Endorsers 1
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // New Endorsers 2
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // New Endorsers 3
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
          }
          else if (!isHCE && !approvalFlowReport?.hasAreaOpsApproved) {
            this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
            this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
        }
        break;

      // case ApprovalState.RIPTScoredSkipped:
      //   if (reportType === ReportType.AnomalyReport)
      //     this.roleTarget = ApprovalUserRole.InspEngSupervisor;
      //   break;

      case ApprovalState.RiskEvaluationScored:
        if (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency || reportType === ReportType.TaskDeferral) { this.roleTarget = ApprovalUserRole.OpsAdvisor; }
        break;

      case ApprovalState.RIPTScored:
        if (reportType === ReportType.AnomalyReport) this.roleTarget = ApprovalUserRole.InspEngSupervisor;

        if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {
          if ((isHCE && abcPrioritization == 'A')) {
            // Asset Type HCEPO
            if (!approvalFlowReport?.hasHCEPOApproved && !endorsers.haveEndorsers) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // New Endorsers 1
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // // New Endorsers 2 ?
            // else if (endorsers.isEndorser2Active) {
            //   this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
            //   this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
            //   this._saveFlowEvents();
            // }
  
            // // New Endorsers 3 ?
            // else if (endorsers.isEndorser3Active) {
            //   this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
            //   this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
            //   this._saveFlowEvents();
            // }
          } 
          
          else {
            this.roleTarget = threatApproverRole;
            this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
        }

        if (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
          if (criticality === 'IC3') {
            // Asset Type HCEPO
            if (!approvalFlowReport?.hasHCEPOApproved && !endorsers.haveEndorsers) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // New Endorsers 1
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
  
            // // New Endorsers 2
            // else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
            //   this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
            //   this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
            //   this._saveFlowEvents();
            // }
  
            // // New Endorsers 3
            // else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
            //   this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
            //   this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
            //   this._saveFlowEvents();
            // }
          }

          else if ((criticality === 'IC2' || criticality === 'IC1' || criticality === 'AC1' || criticality === 'AC2') && !approvalFlowReport?.hasLTAApproved) {
            this.roleTarget = ApprovalUserRole.LocalTechnicalAuthority;
            // FIX: Set ONLY the specific flag, don't accumulate from previous state
            this._state.update(s => ({ ...(s ?? {}), hasLTAApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          else if (!isHCE && !this._approvalFlowReport()?.hasAreaOpsApproved) {
            this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
            // FIX: Set ONLY the specific flag, don't accumulate from previous state
            this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          // TODO: Potentially issue
          // this.roleTarget = ApprovalUserRole.LocalTechnicalAuthority;
        }

        break;

      case ApprovalState.PlanEndorsed:
        // Owner bypass for TDR/ITR/RTR/IDR/RDR: Owner can transition directly to PlanApproved at PlanEndorsed stage.
        // Set roleTarget = Owner and skip normal chain processing to prevent incorrectly
        // setting HCEPO/endorser flags and calling _saveFlowEvents() on their behalf.
        if (
          targetStep === ApprovalState.PlanApproved &&
          getAllMatchingUserApprovalRoles.includes(ApprovalUserRole.GeneralDirector)
        ) {
          this.roleTarget = ApprovalUserRole.GeneralDirector;
          break;
        }

        if (reportType === ReportType.TaskDeferral) {
          // Asset Type HCEPO
          if (!approvalFlowReport?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
            this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
            this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }

          // New Endorsers 1
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }

          // New Endorsers 2
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }

          // New Endorsers 3
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          
          if (auth_level === 'A') {
            if (this._approvalFlowReport()?.hasHCEPOApproved && !this._approvalFlowReport()?.hasAreaOpsApproved) {
              this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
              this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
          }
          if (auth_level === 'B' || auth_level === 'C' || auth_level === 'D') {
            if (this._approvalFlowReport()?.hasHCEPOApproved && !this._approvalFlowReport()?.hasAreaOpsApproved) {
              this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
              this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            } else if (this._approvalFlowReport()?.hasHCEPOApproved && this._approvalFlowReport()?.hasAreaOpsApproved && !this._approvalFlowReport()?.hasProdOpManagerApproved) {
              this.roleTarget = ApprovalUserRole.ProductionOperationManager;
              this._state.update(s => ({ ...(s ?? {}), hasProdOpManagerApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            // this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;

          }
          if (auth_level === 'C' || auth_level === 'D') {
            if (this._approvalFlowReport()?.hasHCEPOApproved && this._approvalFlowReport()?.hasAreaOpsApproved && this._approvalFlowReport()?.hasProdOpManagerApproved && !this._approvalFlowReport()?.hasGenManagerOperatorApproved) {
              this.roleTarget = ApprovalUserRole.GeneralManagerOperation;
              this._state.update(s => ({ ...(s ?? {}), hasGenManagerOperatorApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
          }
          if (auth_level === 'D') {
            if (this._approvalFlowReport()?.hasHCEPOApproved && this._approvalFlowReport()?.hasAreaOpsApproved && this._approvalFlowReport()?.hasProdOpManagerApproved && this._approvalFlowReport()?.hasGenManagerOperatorApproved && !this._approvalFlowReport()?.hasDirectorApproved) {
              this.roleTarget = ApprovalUserRole.GeneralDirector;
              this._state.update(s => ({ ...(s ?? {}), hasDirectorApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
          }

        }

        if (reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
          if (criticality === 'IC3') {
            // Asset Type HCEPO
            if (!approvalFlowReport?.hasHCEPOApproved && endorsers.allApproved) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // // New Endorsers 1
            // else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
            //   this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
            //   this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
            //   this._saveFlowEvents();
            // }

            // New Endorsers 2
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 3
            else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }


            if (this._approvalFlowReport()?.hasHCEPOApproved && !this._approvalFlowReport()?.hasAreaOpsApproved) {
              this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
              this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            // this should be on "Plan Approved"
            else if (approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && !approvalFlowReport?.hasProdOpManagerApproved) {
              this.roleTarget = ApprovalUserRole.ProductionOperationManager;
              this._state.update(s => ({ ...(s ?? {}), hasProdOpManagerApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }



          }
          else if (criticality === 'IC2' || criticality === 'AC2') {
            if (!approvalFlowReport?.hasLTAApproved) {
              this.roleTarget = ApprovalUserRole.LocalTechnicalAuthority;
              this._state.update(s => ({ ...(s ?? {}), hasLTAApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // Asset Type HCEPO
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 1
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 2
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 3
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && !approvalFlowReport?.hasAreaOpsApproved) {
              this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
              this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && !approvalFlowReport?.hasProdOpManagerApproved) {
              this.roleTarget = ApprovalUserRole.ProductionOperationManager;
              this._state.update(s => ({ ...(s ?? {}), hasProdOpManagerApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && approvalFlowReport?.hasProdOpManagerApproved && !approvalFlowReport?.hasGenManagerOperatorApproved) {
              this.roleTarget = ApprovalUserRole.GeneralManagerOperation;
              this._state.update(s => ({ ...(s ?? {}), hasGenManagerOperatorApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

          } else if (criticality === 'IC1' || criticality === 'AC1') {
            if (!approvalFlowReport?.hasLTAApproved) {
              this.roleTarget = ApprovalUserRole.LocalTechnicalAuthority;
              this._state.update(s => ({ ...(s ?? {}), hasLTAApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // Asset Type HCEPO
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
              this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
              this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 1
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 2
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            // New Endorsers 3
            else if (approvalFlowReport?.hasLTAApproved && !approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
              this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
              this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }

            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && !approvalFlowReport?.hasAreaOpsApproved) {
              this.roleTarget = ApprovalUserRole.AreaOpsSuperintendent;
              this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && !approvalFlowReport?.hasProdOpManagerApproved) {
              this.roleTarget = ApprovalUserRole.ProductionOperationManager;
              this._state.update(s => ({ ...(s ?? {}), hasProdOpManagerApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && approvalFlowReport?.hasProdOpManagerApproved && !approvalFlowReport?.hasGenManagerOperatorApproved) {
              this.roleTarget = ApprovalUserRole.GeneralManagerOperation;
              this._state.update(s => ({ ...(s ?? {}), hasGenManagerOperatorApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
            else if (approvalFlowReport?.hasLTAApproved && approvalFlowReport?.hasHCEPOApproved && approvalFlowReport?.hasAreaOpsApproved && approvalFlowReport?.hasProdOpManagerApproved && approvalFlowReport?.hasGenManagerOperatorApproved && !approvalFlowReport?.hasDirectorApproved) {
              this.roleTarget = ApprovalUserRole.GeneralDirector;
              this._state.update(s => ({ ...(s ?? {}), hasDirectorApproved: true }) as IApprovalFlowDto);
              this._saveFlowEvents();
            }
          }

        }

        if (reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat) {

          // Asset Type HCEPO
          if (!approvalFlowReport?.hasHCEPOApproved && (!endorsers.haveEndorsers || endorsers.allApproved)) {
            this.roleTarget = ApprovalUserRole.AssetTypeHCEPO;
            this._state.update(s => ({ ...(s ?? {}), hasHCEPOApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          // New Endorsers 1
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser1Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser1;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser1Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          // New Endorsers 2
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser2Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser2;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser2Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
          // New Endorsers 3
          else if (!approvalFlowReport?.hasHCEPOApproved && endorsers.isEndorser3Active) {
            this.roleTarget = ApprovalUserRole.AdditionalEndorser3;
            this._state.update(s => ({ ...(s ?? {}), additionalEndorser3Endorsed: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          } else {
            this.roleTarget = threatApproverRole;
            this._state.update(s => ({ ...(s ?? {}), hasAreaOpsApproved: true }) as IApprovalFlowDto);
            this._saveFlowEvents();
          }
        }

        break;

      case ApprovalState.PlanApproved:
        this.roleTarget = ApprovalUserRole.ActionItemOwner;

        break;

      case ApprovalState.ResolutionPlanCompleted:
        if (reportType === ReportType.TaskDeferral && targetStep === ApprovalState.Closed) {
          this.roleTarget = ApprovalUserRole.Owner;
        } else {
          this.roleTarget = ApprovalUserRole.Initiator;
        }
        break;

      case ApprovalState.ValidationAndVerificationCompleted:
        if (reportType === ReportType.TaskDeferral || reportType === ReportType.IntegrityThreat || reportType === ReportType.ReliabilityThreat || reportType === ReportType.ReliabilityDeficiency || reportType === ReportType.IntegrityDeficiency) {
          this.roleTarget = ApprovalUserRole.Owner;
        } else if (reportType === ReportType.AnomalyReport) {
          this.roleTarget = ApprovalUserRole.Initiator;
        }
        break;
    }
  };

  public get state() {
    return this._state.asReadonly();
  }

  public save(values: any) {
    const payload = ApprovalFlowDto.fromJS({
      ...values,
      id: values.id ? values.id : undefined,
      informedEmailsTo: values.informedEmailsTo ?? undefined,
      informedEmailsCc: values.informedEmailsCc ?? undefined,
      areaLeadInspEng: values.areaLeadInspEng
        ? values.areaLeadInspEng
        : undefined,
      areaLeadInspEngDescription: values.areaLeadInspEngDescription
        ? values.areaLeadInspEngDescription
        : undefined,
      inspEngSupervisor: values.inspEngSupervisor
        ? values.inspEngSupervisor
        : undefined,
      inspEngSupervisorDescription: values.inspEngSupervisorDescription
        ? values.inspEngSupervisorDescription
        : undefined,
      srOpsConsultant: values.srOpsConsultant
        ? values.srOpsConsultant
        : undefined,
      srOpsConsultantDescription: values.srOpsConsultantDescription
        ? values.srOpsConsultantDescription
        : undefined,
      localTechnicalAuthority: values.localTechnicalAuthority
        ? values.localTechnicalAuthority
        : undefined,
      localTechnicalAuthorityDescription:
        values.localTechnicalAuthorityDescription
          ? values.localTechnicalAuthorityDescription
          : undefined,
      assetTypeHCEPO: values.assetTypeHCEPO ? values.assetTypeHCEPO : undefined,
      assetTypeHCEPODescription: values.assetTypeHCEPODescription
        ? values.assetTypeHCEPODescription
        : undefined,
      areaOpsSuperintendent: values.areaOpsSuperintendent
        ? values.areaOpsSuperintendent
        : undefined,
      areaOpsSuperintendentDescription: values.areaOpsSuperintendentDescription
        ? values.areaOpsSuperintendentDescription
        : undefined,
      productionOperationManager: values.productionOperationManager
        ? values.productionOperationManager
        : undefined,
      productionOperationManagerDescription:
        values.productionOperationManagerDescription
          ? values.productionOperationManagerDescription
          : undefined,
      generalManagerOperation: values.generalManagerOperation
        ? values.generalManagerOperation
        : undefined,
      generalManagerOperationDescription:
        values.generalManagerOperationDescription
          ? values.generalManagerOperationDescription
          : undefined,
      generalDirectorOperation: values.generalDirectorOperation
        ? values.generalDirectorOperation
        : undefined,
      generalDirectorOperationDescription:
        values.generalDirectorOperationDescription
          ? values.generalDirectorOperationDescription
          : undefined,
      generalTabId: values.generalTabId,
      hasAreaLeadApproved: false,
      hasLTAApproved: false,
      hasHCEPOApproved: false,
      hasAreaOpsApproved: false,
      hasProdOpManagerApproved: false,
      hasGenManagerOperatorApproved: false,
      hasDirectorApproved: false
    });

    return this._apiClient.approvalFlowPOST(payload);
  }

  public update(values: any) {
    const state = this._state();
    const generalId = this.general()?.id;
    const payload = ApprovalFlowDto.fromJS({
      ...values,
      informedEmailsTo: values.informedEmailsTo ?? undefined,
      informedEmailsCc: values.informedEmailsCc ?? undefined,
      areaLeadInspEng: values.areaLeadInspEng
        ? values.areaLeadInspEng
        : undefined,
      areaLeadInspEngDescription: values.areaLeadInspEngDescription
        ? values.areaLeadInspEngDescription
        : undefined,
      inspEngSupervisor: values.inspEngSupervisor
        ? values.inspEngSupervisor
        : undefined,
      inspEngSupervisorDescription: values.inspEngSupervisorDescription
        ? values.inspEngSupervisorDescription
        : undefined,
      srOpsConsultant: values.srOpsConsultant
        ? values.srOpsConsultant
        : undefined,
      srOpsConsultantDescription: values.srOpsConsultantDescription
        ? values.srOpsConsultantDescription
        : undefined,
      localTechnicalAuthority: values.localTechnicalAuthority
        ? values.localTechnicalAuthority
        : undefined,
      localTechnicalAuthorityDescription:
        values.localTechnicalAuthorityDescription
          ? values.localTechnicalAuthorityDescription
          : undefined,
      assetTypeHCEPO: values.assetTypeHCEPO ? values.assetTypeHCEPO : undefined,
      assetTypeHCEPODescription: values.assetTypeHCEPODescription
        ? values.assetTypeHCEPODescription
        : undefined,
      areaOpsSuperintendent: values.areaOpsSuperintendent
        ? values.areaOpsSuperintendent
        : undefined,
      areaOpsSuperintendentDescription: values.areaOpsSuperintendentDescription
        ? values.areaOpsSuperintendentDescription
        : undefined,
      productionOperationManager: values.productionOperationManager
        ? values.productionOperationManager
        : undefined,
      productionOperationManagerDescription:
        values.productionOperationManagerDescription
          ? values.productionOperationManagerDescription
          : undefined,
      generalManagerOperation: values.generalManagerOperation
        ? values.generalManagerOperation
        : undefined,
      generalManagerOperationDescription:
        values.generalManagerOperationDescription
          ? values.generalManagerOperationDescription
          : undefined,
      generalDirectorOperation: values.generalDirectorOperation
        ? values.generalDirectorOperation
        : undefined,
      generalDirectorOperationDescription:
        values.generalDirectorOperationDescription
          ? values.generalDirectorOperationDescription
          : undefined,
      generalTabId: values.generalTabId,
      hasAreaLeadApproved: !!state?.hasAreaLeadApproved,
      hasLTAApproved: !!state?.hasLTAApproved,
      hasHCEPOApproved: !!state?.hasHCEPOApproved,
      hasAreaOpsApproved: !!state?.hasAreaOpsApproved,
      hasProdOpManagerApproved: !!state?.hasProdOpManagerApproved,
      hasGenManagerOperatorApproved: !!state?.hasGenManagerOperatorApproved,
      hasDirectorApproved: !!state?.hasDirectorApproved,
    });

    return generalId
      ? this._apiClient.approvalFlowPUT(generalId, payload)
      : EMPTY;
  }

  public confirmStateChange(targetState: ApprovalState) {
    // CONCURRENCY PROTECTION: Prevent multiple simultaneous state changes
    if (this._isProcessingStateChange()) {
      console.warn('[ApprovalFlow] State change already in progress, ignoring duplicate call');
      return of(null);
    }

    const { title, message } = this._defineDialogMessage(targetState);

    const skipValidation = targetState === ApprovalState.Recycle || targetState === ApprovalState.Cancelled || targetState === ApprovalState.OutOfService;

    const collectInvalidControls = (form: any): string[] => {
      if (!form || !form.controls) {
        return [];
      }

      return Object.keys(form.controls).filter((key) => {
        const control = form.controls[key];
        return !!control && !control.disabled && control.invalid;
      });
    };

    if (!skipValidation && !this._formValidatorService.areAllFormsValid()) {

      this._formValidatorService.validateAllForms();
      this._toastr.error(`Some forms are invalid. Please check and correct the errors before submitting. ${this._formValidatorService.getInvalidForms()}`, 'Form Validation Error', { timeOut: 8000 });
      // Release the concurrency lock; otherwise a failed validation leaves it stuck
      // and every retry shows the "A state change is already in progress" (Processing) banner.
      this._isProcessingStateChange.set(false);
      return of([]);
    }

    // Set processing flag only when we are ready to open confirmation flow.
    // This prevents stale lock when returning early on validation errors.
    this._isProcessingStateChange.set(true);

    const dialog = this._dialog.open<ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse | undefined>(ConfirmDialogComponent, {
      data: { title, message },
      width: '500px',                                                           
    });

    return dialog.afterClosed().pipe(
      mergeMap(res => {
        if (!res?.isConfirmed) {
          // User cancelled - release the lock
          this._isProcessingStateChange.set(false);
          return of(null);
        }

        if (targetState === ApprovalState.Closed) {
          this.updateClosedOn.next();
          return this.saveComplete.pipe(
            first(),
            switchMap(() => this._changeState(targetState, res.comment))
          );
        }
        return this._changeState(targetState, res.comment);
      }),
      catchError(err => {
        // Ensure flag is cleared on any error
        this._isProcessingStateChange.set(false);
        throw err;
      })
    );
  }

  public confirmReactivate() {
    if (this._isProcessingStateChange()) {
      return of(null);
    }

    this._isProcessingStateChange.set(true);

    const dialog = this._dialog.open<ConfirmDialogComponent, ConfirmDialogPayload, ConfirmDialogResponse | undefined>(ConfirmDialogComponent, {
      data: {
        title: 'Reactivate',
        message: 'The report will return to the status it had before Out of Service. Do you want to continue?'
      },
      width: '500px',
    });

    return dialog.afterClosed().pipe(
      mergeMap(res => {
        const reportId = this.general()?.id;
        if (!res?.isConfirmed || !reportId) {
          this._isProcessingStateChange.set(false);
          return of(null);
        }

        this._loader.startProcessing();
        return this._apiClient
          .reactivate2(reportId, OutOfServiceActionDto.fromJS({
            approvalUserRole: ApprovalUserRole.Owner,
            comment: res.comment,
          }))
          .pipe(
            map(() => reportId),
            tap(() => {
              this._toastr.success('The report has been reactivated');
              this.refreshCurrentState();
              this.flowEventsUpdated.next();
            }),
            catchError(err => {
              if (Array.isArray(err?.errors)) {
                err.errors.forEach((error: string) => this._toastr.error(error));
              } else {
                this._toastr.error('Reactivation failed.', 'Error');
              }
              return of(null);
            }),
            finalize(() => {
              this._loader.stopProcessing();
              this._isProcessingStateChange.set(false);
            })
          );
      })
    );
  }

  private _defineDialogMessage(targetState: ApprovalState) {
    let title: string | null = null;
    let message: string | null = null;

    switch (targetState) {
      case ApprovalState.DraftedForReview:
        title = 'Drafted for Review';
        message = 'Do you want to confirm drafting for review?';
        break;
      case ApprovalState.PlanApproved:
        title = 'Plan Approved';
        message = 'Do you want to approve this plan?';
        break;
      case ApprovalState.PlanEndorsed:
        title = 'Plan Endorsed';
        message = 'Do you want to endorse this plan?';
        break;
      case ApprovalState.PlanConfirmed:
        title = 'Plan Confirmed';
        message = 'Do you want to confirm this plan?';
        break;
      case ApprovalState.RIPTScored:
        title = 'RIPT Scoring';
        message = 'Do you want to confirm RIPT scoring?';
        break;
      // case ApprovalState.RIPTSkipped:
      //   title = 'RIPT Skipped';
      //   message = 'Do you want to skip RIPT scoring?';
      //   break;
      case ApprovalState.RiskEvaluationScored:
        title = 'Risk Evaluation Scored';
        message = 'Do you want to confirm risk evaluation scoring?';
        break;
      case ApprovalState.ResolutionPlanCompleted:
        title = 'Resolution Plan Completed';
        message = 'Do you want to confirm resolution plan completion?';
        break;
      case ApprovalState.SubmittedToOpsAdvisor:
        title = 'Submitted to Operations Advisor';
        message = 'Do you want to confirm submission to Operations Advisor?';
        break;
      case ApprovalState.ValidationAndVerificationCompleted:
        title = 'Verification and Validation';
        message = 'Do you want to confirm verification and validation?';
        break;
      case ApprovalState.Recycle:
        title = 'Recycle';
        message = 'Do you want to recycle this item?';
        break;
      case ApprovalState.Cancelled:
        title = 'Cancel';
        message = 'Do you want to cancel this item?';
        break;
      case ApprovalState.Closed:
        title = 'Close';
        message = 'Do you want to close this item?';
        break;
      case ApprovalState.OutOfService:
        title = 'Out of Service';
        message = 'Do you want to confirm out of service?';
        break;
      default:
        title = 'Confirm State Change';
        message = 'Do you want to confirm this state change?';
        break;
    }

    return { title, message };
  }

  private _changeState(targetState: ApprovalState, comment?: string) {
    const reportId = this.general()?.id;
    this._loader.startProcessing();
    
    // RACE CONDITION PROTECTION: Create snapshot before any state modifications
    const currentApprovalFlow = this._approvalFlowReport();
    if (currentApprovalFlow) {
      this._stateSnapshot = ApprovalFlowDto.fromJS({...currentApprovalFlow});
    }

    // Update state, but defer the approval-flow PUT so it only fires AFTER
    // the state-change PUT succeeds.
    this._flowSavePending = false;
    this._deferFlowSave = true;
    this.userRole(targetState);
    this._deferFlowSave = false;

    if (!reportId) {
      this._loader.stopProcessing();
      this._isProcessingStateChange.set(false);
      return of(reportId);
    }

    const payload = ChangeApprovalStateDto.fromJS({
      comment,
      reportId,
      targetState,
      approvalUserRole: this.roleTarget ?? undefined,
    });

    // Non-admin owners are not allowed by the generic state endpoint while the report is Out of Service
    const request$ = this.general()?.currentState === ApprovalState.OutOfService && targetState === ApprovalState.Cancelled
      ? this._apiClient.cancel(reportId, OutOfServiceActionDto.fromJS({
        approvalUserRole: ApprovalUserRole.Owner,
        comment,
      }))
      : this._apiClient.state(reportId, payload as any);

    return request$
      .pipe(
        map(() => reportId),
        tap(() => {
          // Success: Clear snapshot and notify
          this._stateSnapshot = null;
          this._toastr.success(`You've successfully completed the action "${this._targetStateNaming(targetState)}"`);
          this._loader.stopProcessing();
          this.flowEventsUpdated.next();

          // Now that the state change succeeded, persist the deferred approval-flow events.
          if (this._flowSavePending) {
            this._flowSavePending = false;
            this._saveFlowEvents();
          }
        }),
        catchError(err => {
          // ROLLBACK: Restore state from snapshot on error
          this._rollbackState();
          // Discard the deferred flow-events save; the state change failed.
          this._flowSavePending = false;
          
          if ('errors' in err) {
            const errors: string[] = err.errors;
            errors.forEach(error => this._toastr.error(error));
          } else {
            this._toastr.error('State change failed. Changes have been reverted.', 'Error');
          }
          
          this._loader.stopProcessing();
          return of(err);
        }),
        finalize(() => {
          // ALWAYS clear processing flag, regardless of success or error
          this._isProcessingStateChange.set(false);
        })
      );
  }

  /**
   * Rollback state to snapshot when operation fails
   */
  private _rollbackState() {
    if (this._stateSnapshot) {
      console.warn('[ApprovalFlow] Rolling back state to snapshot due to error');
      
      // Restore the snapshot to the store
      this._store.setApprovalFlow(this._stateSnapshot);
      
      // Reset local state
      this._state.set(null);
      
      // Clear snapshot
      this._stateSnapshot = null;
      
      // Notify listeners of the rollback
      this.flowEventsUpdated.next();
      
      this._toastr.info('Approval flow state has been restored', 'Rollback');
    }
  }

  public reset() {
    // this._approvalFlowReport.set(null);
    this._state.set(null);
  }

  completeAI(aiId: number, status: ChangeActionitemStatusRequestDto) {
    return this._apiClient.status(aiId, status);
  }

  reActivateAI(aiId: number, status: ChangeActionitemStatusRequestDto) {
    return this._apiClient.reactivate(aiId, ReactivateActionItemRequestDto.fromJS({
      comment: status.comment ?? ''
    }));
  }

  setApprovalUserRole(value: ApprovalUserRole) {
    this._approvalUserRole.set(value);
  }


  // private _getFlowEvents() {

  //   const store = this._approvalFlowReport();

  //   return store;
  // }

  private _saveFlowEvents() {
    // DEFERRAL: When invoked during a state change, defer the API call until
    // the state-change PUT succeeds. Only record that a save is pending.
    if (this._deferFlowSave) {
      this._flowSavePending = true;
      return;
    }

    // CONCURRENCY PROTECTION: Prevent concurrent saves
    if (this._saveFlowEventsInProgress()) {
      console.warn('[ApprovalFlow] Save flow events already in progress, ignoring duplicate call');
      return;
    }

    const state = this._state();
    const generalId = this.general()?.id;
    const approvalFlow = this._approvalFlowReport();
    
    if (!state || !generalId || !approvalFlow) {
      return;
    }

    // Set flag to prevent concurrent calls
    this._saveFlowEventsInProgress.set(true);

    const updatedApprovalFlow = ApprovalFlowDto.fromJS({
      ...approvalFlow,
      hasAreaLeadApproved: !approvalFlow.hasAreaLeadApproved ? state.hasAreaLeadApproved : approvalFlow.hasAreaLeadApproved,
      hasLTAApproved: !approvalFlow.hasLTAApproved ? state.hasLTAApproved : approvalFlow.hasLTAApproved,
      hasHCEPOApproved: !approvalFlow.hasHCEPOApproved ? state.hasHCEPOApproved : approvalFlow.hasHCEPOApproved,
      hasAreaOpsApproved: !approvalFlow.hasAreaOpsApproved ? state.hasAreaOpsApproved : approvalFlow.hasAreaOpsApproved,
      hasProdOpManagerApproved: !approvalFlow.hasProdOpManagerApproved ? state.hasProdOpManagerApproved : approvalFlow.hasProdOpManagerApproved,
      hasGenManagerOperatorApproved: !approvalFlow.hasGenManagerOperatorApproved ? state.hasGenManagerOperatorApproved : approvalFlow.hasGenManagerOperatorApproved,
      hasDirectorApproved: !approvalFlow.hasDirectorApproved ? state.hasDirectorApproved : approvalFlow.hasDirectorApproved,
      additionalEndorser1Endorsed: !approvalFlow.additionalEndorser1Endorsed ? state.additionalEndorser1Endorsed : approvalFlow.additionalEndorser1Endorsed,
      additionalEndorser2Endorsed: !approvalFlow.additionalEndorser2Endorsed ? state.additionalEndorser2Endorsed : approvalFlow.additionalEndorser2Endorsed,
      additionalEndorser3Endorsed: !approvalFlow.additionalEndorser3Endorsed ? state.additionalEndorser3Endorsed : approvalFlow.additionalEndorser3Endorsed,
    });
    
    console.log('[_saveFlowEvents] Payload being sent to backend:', JSON.stringify(updatedApprovalFlow));

    this._apiClient.approvalFlowPUT(generalId, updatedApprovalFlow)
      .pipe(
        tap(() => {
          // Update store on success
          this._store.setApprovalFlow(updatedApprovalFlow);
          this.flowEventsUpdated.next();
        }),
        catchError(err => {
          console.error('[ApprovalFlow] Failed to save flow events to backend:', err);
          this._toastr.error('Failed to save approval flow events. Please try again.', 'Save Error');
          // Note: Rollback will be handled by _changeState if needed
          return of(null);
        }),
        tap(() => {
          // Always clear the flag in tap to ensure it runs
          this._saveFlowEventsInProgress.set(false);
        })
      )
      .subscribe();
  }

  private _targetStateNaming(targetState: ApprovalState) {
    switch (targetState) {
      case ApprovalState.Draft:
        return 'Draft';
      case ApprovalState.DraftedForReview:
        return 'Drafted for Review';
      case ApprovalState.SubmittedToOpsAdvisor:
        return 'Submit to Operations Advisor';
      case ApprovalState.PlanConfirmed:
        return 'Plan confirmed';
      case ApprovalState.RIPTScored:
        return 'RIPT scored';
      case ApprovalState.RiskEvaluationScored:
        return 'Risk Evaluation Scored';
      // case ApprovalState.RIPTSkipped:
      //   return 'RIPT Skipped';
      case ApprovalState.PlanEndorsed:
        return 'Plan Endorsed';
      case ApprovalState.PlanApproved:
        return 'Plan Approved';
      case ApprovalState.ResolutionPlanCompleted:
        return 'Resolution Plan Completed';
      case ApprovalState.ValidationAndVerificationCompleted:
        return 'Validation and Verification Completed';
      case ApprovalState.Closed:
        return 'Closed';
      case ApprovalState.Cancelled:
        return 'Cancelled';
      case ApprovalState.OutOfService:
        return 'Out of Service';
      case ApprovalState.Recycle:
        return 'Recycle';
      default:
        return '';
    }
  }

  /**
   * Indicates whether a current-state refresh request is in flight.
   */
  readonly isRefreshing = signal<boolean>(false);

  /**
   * Fetches the latest ApprovalState from the server and patches the store.
   * Calls GET /api/GeneralTab/{id}/current-state
   */
  refreshCurrentState(): void {
    const id = this.general()?.id;
    if (!id) return;

    this.isRefreshing.set(true);
    this._apiClient
      .currentState(id)
      .pipe(
        first(),
        finalize(() => this.isRefreshing.set(false))
      )
      .subscribe({
        next: (state: ApprovalState) => {
          if (state !== null && state !== undefined) {
            this._store.patchCurrentState(state);
          }
        },
        error: () => {
          this._toastr.error('Failed to refresh lifecycle state. Please try again.');
        }
      });
  }}